import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCourseAccess } from '../hooks/useCourseAccess';
import { CourseAccessGuard } from '../components/CourseAccessGuard';
import { DashboardLayout } from '../components/DashboardLayout';
import { MODULES } from '../data/modules';

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { 
    courseAccess, 
    userBatch,
    isUni, 
    isFirst, 
    isManager, 
    isPreviousBatch,
    isCurrentBatch,
    getAccessLevelName,
    getAccessLevelDescription,
    getAvailableModulesByDate,
    getModuleStatus,
    getAvailableLiveSessions
  } = useCourseAccess();

  const getAccessLevelBadge = () => {
    if (isManager) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
          Managers
        </span>
      );
    } else if (isFirst) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
          First Jobbers
        </span>
      );
    } else if (isUni) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          University Students
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
          No Access
        </span>
      );
    }
  };

  const availableModules = getAvailableModulesByDate();

  return (
    <DashboardLayout>
      <div className="py-6 bg-[#FCFAF7] min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <h1 className="text-2xl font-semibold text-[#203D32] font-alegreya">Dashboard</h1>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="py-4">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-[#203D32] font-alegreya">
                  Welcome back, {user?.name}!
                </h2>
                {getAccessLevelBadge()}
              </div>
              
              {/* Course Access Information */}
              <div className="mb-8 p-4 bg-[#FCFAF7] rounded-lg border border-[#E5EFE7]">
                <h3 className="text-lg font-semibold text-[#203D32] mb-2">Course Access Level</h3>
                <p className="text-gray-700">
                  Current access: <span className="font-medium">{getAccessLevelName()}</span>
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {getAccessLevelDescription()}
                </p>
                
                {/* Batch-specific access information */}
                <div className="mt-3 p-3 bg-white rounded-md border border-gray-200">
                  {isPreviousBatch ? (
                    <div className="text-sm">
                      <p className="text-green-700 font-medium">✅ Lifetime Access</p>
                      <p className="text-gray-600">As a previous batch member, you have unrestricted access to all your modules.</p>
                    </div>
                  ) : (
                    <div className="text-sm">
                      <p className="text-blue-700 font-medium">📅 Scheduled Release</p>
                      <p className="text-gray-600">As the current batch, modules will be released according to the schedule.</p>
                    </div>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mt-2">
                  Available modules: {availableModules.length > 0 ? availableModules.map(m => m.id).join(', ') : 'None available yet'}
                </p>
              </div>
              
              {/* Course Progress */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-bold text-[#203D32] mb-6 font-alegreya">Your Progress</h3>
                  <div className="space-y-6">
                    {getAvailableModulesByDate().map(module => {
                      const status = getModuleStatus(module);
                      
                      return (
                        <div key={module.id}>
                          <div className="flex justify-between mb-2">
                            <div className="flex-1">
                              <span className="text-lg font-medium text-[#203D32]">
                                Module {module.id}: {module.title}
                              </span>
                              <div className="flex items-center mt-1 space-x-4">
                                <span className="text-sm text-gray-500">
                                  {module.videos.length} videos
                                </span>
                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${status.color}`}>
                                  {status.label}
                                </span>
                              </div>
                            </div>
                            <span className="text-lg font-medium text-[#203D32]">0%</span>
                          </div>
                          <div className="w-full bg-[#E5EFE7] rounded-full h-3">
                            <div className="bg-[#203D32] h-3 rounded-full" style={{ width: '0%' }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Upcoming Live Sessions */}
                <div>
                  <h3 className="text-xl font-bold text-[#203D32] mb-6 font-alegreya">Your Live Sessions - Batch {userBatch}</h3>
                  <div className="space-y-4">
                    {getAvailableLiveSessions().map((session) => (
                      <div key={session.id} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-lg font-semibold text-blue-800 mb-2">{session.title}</h4>
                            {session.description && (
                              <p className="text-sm text-blue-600 mb-2">{session.description}</p>
                            )}
                            <div className="flex items-center space-x-4 text-blue-700">
                              <div className="flex items-center">
                                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span>{session.date}</span>
                              </div>
                              <div className="flex items-center">
                                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>{session.time}</span>
                              </div>
                            </div>
                          </div>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            Upcoming
                          </span>
                        </div>
                      </div>
                    ))}

                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                      <div className="flex items-center">
                        <svg className="h-5 w-5 text-yellow-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-yellow-800 text-sm">
                          <strong>Note:</strong> All live sessions will be held on Zoom. If you haven't gotten a Calendar Invite please contact the Shaun Dju Academy team at +62 881 080 545 047.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Links */}
                <div>
                  <h3 className="text-xl font-bold text-[#203D32] mb-6 font-alegreya">Quick Links</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    <a
                      href="/modules"
                      className="relative rounded-xl border-2 border-[#E5EFE7] bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-[#203D32] transition-colors duration-200"
                    >
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-[#203D32]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="absolute inset-0" aria-hidden="true" />
                        <p className="text-lg font-medium text-[#203D32]">View Modules</p>
                        <p className="text-base text-gray-500">Access your course materials</p>
                      </div>
                    </a>

                    <a
                      href="/live-sessions"
                      className="relative rounded-xl border-2 border-[#E5EFE7] bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-[#203D32] transition-colors duration-200"
                    >
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-[#203D32]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="absolute inset-0" aria-hidden="true" />
                        <p className="text-lg font-medium text-[#203D32]">Live Sessions</p>
                        <p className="text-base text-gray-500">Ask questions and participate</p>
                      </div>
                    </a>

                    <a
                      href="https://discord.gg/JK6XEtW6Pb"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative rounded-xl border-2 border-[#E5EFE7] bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-[#203D32] transition-colors duration-200"
                    >
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-[#203D32]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="absolute inset-0" aria-hidden="true" />
                        <p className="text-lg font-medium text-[#203D32]">Join Discord</p>
                        <p className="text-base text-gray-500">Connect with other members</p>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}; 